import React from 'react';
import { Paper, Table, TableBody, TableContainer, Typography, Box, TableRow, TableCell } from '@mui/material';
import { PayrollSpreadsheetHeader } from './PayrollSpreadsheetHeader';
import { PayrollDataRow } from './PayrollDataRow';
import type { MonthlyPayrollData } from '../../types/employee';
import { groupEmployeesByPayType } from '../../lib/payroll/display-helper';
import { EmptyState } from '@/components/ui';

interface PayrollSpreadsheetProps {
  activeData: MonthlyPayrollData[];
  retiredData: MonthlyPayrollData[];
  onPayslipClick: (data: MonthlyPayrollData) => void;
}

const SectionHeaderRow: React.FC<{ label: string; colSpan: number }> = ({ label, colSpan }) => (
  <TableRow>
    <TableCell
      colSpan={colSpan}
      sx={{
        bgcolor: 'var(--bg-muted)',
        py: 1,
        px: 1.5,
        borderBottom: '1px solid var(--border-default)',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--fg-secondary)' }}>
        {label}
      </Typography>
    </TableCell>
  </TableRow>
);

const COL_COUNT = 11;

function groupPayrollData(data: MonthlyPayrollData[]): Array<{ label: string; rows: MonthlyPayrollData[] }> {
  const employees = data.map((d) => d.employee);
  const sections = groupEmployeesByPayType(employees);
  const dataById = new Map(data.map((d) => [d.employee.id, d]));

  return sections
    .map((section) => ({
      label: section.label,
      rows: section.employees
        .map((e) => dataById.get(e.id))
        .filter((d): d is MonthlyPayrollData => !!d),
    }))
    .filter((s) => s.rows.length > 0);
}

export const PayrollSpreadsheet: React.FC<PayrollSpreadsheetProps> = ({
  activeData,
  retiredData,
  onPayslipClick,
}) => {
  const activeSections = groupPayrollData(activeData);
  const showSectionHeaders = activeSections.length > 1;

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)', overflow: 'auto' }}>
        <Table stickyHeader size="small" sx={{ tableLayout: 'auto', minWidth: 1100 }}>
          <PayrollSpreadsheetHeader />
          <TableBody>
            {activeSections.map((section) => (
              <React.Fragment key={section.label}>
                {showSectionHeaders && (
                  <SectionHeaderRow label={section.label} colSpan={COL_COUNT} />
                )}
                {section.rows.map((data) => (
                  <PayrollDataRow
                    key={data.employee.id}
                    data={data}
                    muted={false}
                    onPayslipClick={onPayslipClick}
                  />
                ))}
              </React.Fragment>
            ))}
            {retiredData.length > 0 && (
              <>
                <SectionHeaderRow label="퇴직" colSpan={COL_COUNT} />
                {retiredData.map((data) => (
                  <PayrollDataRow
                    key={data.employee.id}
                    data={data}
                    muted
                    onPayslipClick={onPayslipClick}
                  />
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {activeData.length === 0 && retiredData.length === 0 && (
        <EmptyState title="급여 데이터가 없습니다" />
      )}
    </Paper>
  );
};
